import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { UncontrolledTooltip } from 'reactstrap';
import { format } from 'date-fns';

import { AdminCountryInfoDto } from '@repo/common-types';
import CountryQuickActions from './components/CountryQuickActions';
import { ColumnAlignment } from '@/common/tableData/DataTable';

export const createColumns = (
  onEdit: (countryId: string) => void,
  onDelete: (countryId: string) => void,
): ColumnDef<AdminCountryInfoDto, unknown>[] => [
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <CountryQuickActions
        country={row.original}
        onEdit={() => onEdit(row.original.id)}
        onDelete={() => onDelete(row.original.id)}
      />
    ),
    size: 80,
    enableSorting: false,
    meta: {
      alignment: ColumnAlignment.CENTER,
    },
  },
  {
    accessorKey: 'name',
    header: 'Country Name',
    cell: ({ row }) => {
      const tooltipId = `country-name-${row.original.id}`;
      const name = row.original.name;
      const code = row.original.code;

      return (
        <>
          <div className="d-flex flex-column" id={tooltipId}>
            <span className="fw-medium">{name}</span>
            <small className="text-muted">{code}</small>
          </div>
          <UncontrolledTooltip placement="top" target={tooltipId}>
            <div>
              <div>{name}</div>
              <div>{code}</div>
            </div>
          </UncontrolledTooltip>
        </>
      );
    },
    meta: {
      alignment: ColumnAlignment.LEFT,
    },
    enableSorting: true,
  },
  {
    accessorKey: 'subdivisionsCount',
    header: 'Subdivisions',
    cell: ({ row }) => {
      const count = row.original.subdivisionsCount;
      const hasSubdivisions = row.original.hasSubdivisions;

      return <span className="fw-medium">{hasSubdivisions ? count : '-'}</span>;
    },
    meta: {
      alignment: ColumnAlignment.RIGHT,
    },
    enableSorting: true,
  },
  {
    accessorKey: 'regionsCount',
    header: 'Region Assignments',
    cell: ({ row }) => {
      const regions = row.original.regions;
      const count = row.original.regionsCount;

      if (count === 0) {
        return <span className="text-muted">No regions</span>;
      }

      return <span className="fw-medium">{count}</span>;
    },
    meta: {
      alignment: ColumnAlignment.RIGHT,
    },
    enableSorting: true,
  },
  {
    accessorKey: 'updatedAt',
    header: 'Date Updated',
    cell: ({ row }) => {
      const date = new Date(row.original.updatedAt);
      return (
        <div className="d-flex flex-column">
          <span className="fw-medium">{format(date, 'yyyy-MM-dd')}</span>
          <small className="text-muted">{format(date, 'HH:mm')}</small>
        </div>
      );
    },
    meta: {
      alignment: ColumnAlignment.LEFT,
    },
  },
];
